<script>
  export let openedMenu
</script>


<main>

  <h2>About the Polar Rock Repository</h2>
  <p>
    The Polar Rock Repository (PRR), supported by the National Science Foundation's Office of Polar Programs, contains rock samples from Antarctica and the southern oceans along with small collections from South America, Africa, and Australia. The PRR collection also includes field notes, maps, thin sections, as well as an extensive media archive.
  </p>
  <p>
    Rock samples may be used for research by university scientists globally as well as for educational or museum use in the United States. <b>Destructive techniques are allowed</b>. We are happy to assist in finding samples and encourage users to reach out via email or schedule a virtual meeting to discuss the collection. Visitors are also welcome at the PRR by appointment.
  </p>
  <p>
    DOI: <a target="_blank" href="https://doi.org/doi:10.7289/V5RF5S18">https://doi.org/doi:10.7289/V5RF5S18</a><br>
    RRID:SCR_002212
  </p>

   <iframe src="https://player.vimeo.com/video/340791505" class="embed-responsive-item" allow="fullscreen" allowfullscreen title="15 Years of the Polar Rock Repository"></iframe>
  <br><br>

  <h2>Our Team</h2>
  <b><a href="https://byrd.osu.edu/people/grunow.1" target="_blank" rel="noopener noreferrer">Anne Grunow</a></b>, Director and Senior Research Scientist<br>
  <b><a href="https://byrd.osu.edu/people/maletic.2" target="_blank" rel="noopener noreferrer">Erica Maletic</a></b>, Curator and Research Scientist<br>
  <br><br><br>


<h1>Guidelines</h1>
  <h2>Acknowledgement</h2>
  <p>
    If you utilize any PRR samples, database or photo archive information in conducting your research, please acknowledge the PRR and the National Science Foundation:<br>
  </p>
    <blockquote style="color: #545454;margin-left: 7px; margin-bottom: 25px; border-left: 2px solid #777;padding-left: 15px;"><b>This material is based on services provided by the Polar Rock Repository with support from the National Science Foundation, under Award <a href="https://www.nsf.gov/awardsearch/showAward?AWD_ID=2436582">OPP-2436582</a>.</b></blockquote>

  <div style="float: right; width: 20%; margin: 0; padding-right: 25px; text-align: center;">
    <a href="../images/prr_logo.png"><img src="../images/prr_logo.png" width=100% height=auto alt="PRR Logo" /></a>
  </div>
  <p>
    Logo files for the PRR are available to download for use in presentations and documents: <a href="../images/prr_logo.png">PNG</a> | <a href="../images/prr_logo.eps">EPS</a>
  </p>

  <p><small>See our
    <span
      style="color: #960000; text-decoration: underline; cursor: pointer;"
      on:click={() => {openedMenu = "Policy"}}
    >Policy for Sample Donation, Distribution, and Publication</span>.</small></p>
  <br><br>

  <h2>Loans</h2>

  <p>The PRR offers loans of its samples for research, education, or museum exhibit use.</p>
  <br>

<details>
  <summary><h3>Research Loans</h3></summary>
  <p>
    <strong>Researchers</strong> may request loans online via the <em>Add to Sample Bag</em> feature. A button will appear on the sample's description page 
    when <a href="https://prr.osu.edu/collection/search">searching our database</a>. When all of the samples have been added to the Sample Bag, an online Sample Request Form asks for a research description and planned analytical techniques. Samples are shipped, at no cost, to the Researcher.
  </p>
  <p>
    Requests from scientists outside the United States will be considered if the request would not reduce the future utility of the collection for US scientists. In most cases, relatively small requests that are not focused on unusually scarce samples will be able to be filled.
  </p>
  <p>
    <strong>Researchers</strong> may borrow samples for up to 3 years. Any unused portions of the sample (including residues) must be returned.
  </p>
  <p>
    <strong>Researchers</strong> must:
  </p>
  <ol>
    <li>Acknowledge the PRR in any proposal or publication, including annual and final NSF reports (see below for a suggested statement),</li>
    <li>In the acknowledgements, add:<br>This material is based on services provided by the Polar Rock Repository with support from the National Science Foundation, under Award <a href="https://www.nsf.gov/awardsearch/showAward?AWD_ID=2436582">OPP-2436582</a>. DOI: <a href="https://doi.org/doi:10.7289/V5RF5S18">https://doi.org/doi:10.7289/V5RF5S18</a></li>
    <li>Acknowledge the original collector of the sample(s),</li>
    <li>Include the "Polar Rock Repository" as key words provided to the journal or book publisher using any PRR samples or data,</li>
    <li>Provide reprints to the PRR of reports/publications that used PRR samples.</li> 
  </ol>
  </details>
  <br>

  <details>
  <summary><h3>Educational and Museum Loans</h3></summary>
  <p>
    <strong>Educators</strong> may borrow, at no cost, a <a href="https://go.osu.edu/rockbox">Polar Rock Box</a> containing ~30 representative rocks from igneous, metamorphic, 
    sedimentary rocks plus fossils and minerals to teach about geology. The rock box is designed for K-6 education.
  </p>
  <p>
    <strong>Educators and Museums</strong> may request sample loans via the PRR website search by adding samples to the Sample Bag and filling out the online Sample Request Form. Samples may be borrowed for up to 1 year with the option for renewal.
  </p>
    <p><small>See our
    <span
      style="color: #960000; text-decoration: underline; cursor: pointer;"
      on:click={() => {openedMenu = "Policy"}}
    >Policy for Sample Donation, Distribution, and Publication</span>
  .</small></p>
  </details>
  <br><br>


  <h2>Sample Donations</h2>

  <p>
    The PRR welcomes donations of scientifically valuable polar rock collections and any supporting material. Scientists interested in donating samples should first contact the <a href="mailto:BPRC-PRR_Curator@osu.edu?cc=grunow.1@osu.edu">Curator</a> about the appropriateness of their collection for this facility. No more than 25kg will be accepted for each sample unless the sample was obtained from areas difficult to access logistically, ‘type’ localities, areas designated as sites of special scientific interest, etc.
  </p>
  <ul>
    <li>Donors are responsible for entering their sample data into a <a href="../downloads/PRR_sample_template.xlsx">pre-designed Excel spreadsheet</a> (XLSX format) or by providing field notebooks or other notes to the curator prior to sample shipment.</li>
    <li>Funds are available to cover the sample shipping costs once the sample metadata have been provided to the PRR. Additional data, such as chemical analyses, thin sections, structural data, maps, photos, etc., are requested by the PRR.</li>
    <li>The donor relinquishes all exclusive claims for use and control of the donated materials.</li>
  </ul>
  <p><small>See our
    <span
      style="color: #960000; text-decoration: underline; cursor: pointer;"
      on:click={() => {openedMenu = "Policy"}}
    >Policy for Sample Donation, Distribution, and Publication</span>.</small></p>

</main>



<style>
  main {
    line-height: 1.22;
    font-family: Verdana, Arial, Helvetica, sans-serif;
    font-size: 10pt;
    font-weight: 400;
    color: #212529;
  }

  h1 {
    font-size: 26pt;
    font-family: 'proximanova', 'Source Sans Pro', sans-serif;
    font-weight: 600;
    border-bottom: 1px solid;
    margin-top: 0;
    margin-bottom: 5px;
  }
  details {
    margin-bottom: 2em;
  }

  summary {
    cursor: pointer;
    border-bottom: 1px solid;
  }

  h2 {
    font-size: 18pt;
    font-family: 'proximanova', 'Source Sans Pro', sans-serif;
    font-weight: 600;
    border-bottom: 1px solid;
    margin-top: 0;
  }

  summary h3 {
    display: inline;
    border-bottom: none;
  }

  h3 {
    font-size: 14pt;
    font-family: 'proximanova', 'Source Sans Pro', sans-serif;
    font-weight: 600;
    border-bottom: 1px solid;
    margin-top: 0;
  }

  a {
    color: #960000;
    text-decoration: underline;
  }

  li{
    margin: 0 0 10px 0;
  }
</style>
